import { useEffect, useEffectEvent, useRef, useState } from 'react'

import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/customer/components/ui/input-otp'
import { isAuthError } from '@/customer/lib/auth'

const OTP_CODE_LENGTH = 6
/** Cooldown seconds between OTP sends. Purely a UX guard; the auth provider enforces its own server-side rate limit. */
const OTP_RESEND_COOLDOWN_SECONDS = 60

/**
 * Owns the state machine for an OTP verification step: input value,
 * verification pending flag, error code, and the resend cooldown timer.
 *
 * The caller supplies two thunks - `sendCode` and `verifyCode` - and
 * decides how to render everything around the input (copy, resend
 * button, error banner). Both thunks are expected to throw on failure;
 * `isAuthError` extracts the code from better-auth's shape and stashes
 * it on `errorCode`.
 *
 * When `autoSend` is true, `sendCode` fires on mount (guarded against
 * React StrictMode double-fire). Otherwise the caller is expected to
 * have already dispatched a code before mounting.
 */
export function useOtpFlow({
  sendCode,
  verifyCode,
  onSuccess,
  autoSend = false,
}: {
  sendCode: () => Promise<void>
  verifyCode: (code: string) => Promise<void>
  onSuccess: () => void
  autoSend?: boolean
}) {
  const [otpCode, setOtpCode] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const [errorCode, setErrorCode] = useState<string | null>(null)
  // Seeded at full cooldown because the hook is only ever mounted when
  // a code has just been sent (either via `autoSend` about to fire, or
  // by the caller before mount). Prevents an immediate resend flood.
  const [secondsRemaining, setSecondsRemaining] = useState(OTP_RESEND_COOLDOWN_SECONDS)
  // Guards `handleVerify` against a same-frame double invocation from
  // `setOtpCode(fullCode)` firing twice before `isVerifying` has
  // flipped. State reads inside `setOtpCode` are stale by definition;
  // a ref is the only reliable latch.
  const verifyingRef = useRef(false)

  useEffect(() => {
    if (secondsRemaining <= 0) return
    const id = window.setTimeout(() => setSecondsRemaining((s) => s - 1), 1000)
    return () => window.clearTimeout(id)
  }, [secondsRemaining])

  // `useEffectEvent` reads the latest `sendCode` without making the
  // auto-send effect depend on it - callers can pass an inline thunk
  // without needing `useCallback`.
  const dispatch = useEffectEvent(async () => {
    setErrorCode(null)
    setOtpCode('')
    try {
      await sendCode()
      setSecondsRemaining(OTP_RESEND_COOLDOWN_SECONDS)
    } catch (err) {
      setErrorCode(isAuthError(err) ? err.code : null)
      // Nothing was sent, so don't hold the resend button hostage to
      // the cooldown that would normally follow a successful send.
      setSecondsRemaining(0)
    }
  })

  // Ref-based StrictMode guard: the initial send must only fire once
  // even when the effect runs twice on mount in dev.
  const sentRef = useRef(false)
  useEffect(() => {
    if (!autoSend || sentRef.current) return
    sentRef.current = true
    void dispatch()
  }, [autoSend])

  async function handleVerify(code: string) {
    verifyingRef.current = true
    setIsVerifying(true)
    try {
      await verifyCode(code)
      onSuccess()
    } catch (err) {
      setErrorCode(isAuthError(err) ? err.code : null)
    } finally {
      verifyingRef.current = false
      setIsVerifying(false)
    }
  }

  return {
    otpCode,
    isVerifying,
    errorCode,
    secondsRemaining,
    setOtpCode(value: string) {
      setOtpCode(value)
      if (value.length === OTP_CODE_LENGTH && !verifyingRef.current) void handleVerify(value)
    },
    resend() {
      if (secondsRemaining > 0) return
      void dispatch()
    },
  }
}

/**
 * 6-slot OTP input styled to match the rest of the auth flow. Thin
 * wrapper around shadcn's `InputOTP` that pre-configures the slot
 * count and layout so callers don't repeat the `<InputOTPGroup>`
 * boilerplate.
 */
export function OtpInput({
  value,
  onChange,
  disabled,
}: {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
}) {
  return (
    <InputOTP
      autoFocus
      maxLength={OTP_CODE_LENGTH}
      value={value}
      onChange={onChange}
      disabled={disabled}
    >
      <InputOTPGroup>
        {Array.from({ length: OTP_CODE_LENGTH }).map((_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length static list, indices are stable
          <InputOTPSlot key={i} index={i} />
        ))}
      </InputOTPGroup>
    </InputOTP>
  )
}
